import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { GetInstructorsResult } from '../../api/get-instructors/getInstructors.types';
import type {
  PostInstructorRequestBody,
  PostInstructorRequestResponse,
} from '../../api/post-instructor-request/postInstructorRequest.types';
import type {
  UsePostInstructorRequestParams,
  UsePostInstructorRequestResult,
} from './usePostInstructorRequest.types';

import { postInstructorRequest } from '../../api/post-instructor-request/postInstructorRequest';

export function usePostInstructorRequest(
  params: UsePostInstructorRequestParams
): UsePostInstructorRequestResult {
  const queryClient = useQueryClient();

  return useMutation<
    PostInstructorRequestResponse,
    Error,
    PostInstructorRequestBody
  >({
    mutationFn: (
      body: PostInstructorRequestBody
    ): Promise<PostInstructorRequestResponse> =>
      postInstructorRequest(params.customerId, params.instructorId, body),
    onSuccess: (
      result: PostInstructorRequestResponse,
      variables: PostInstructorRequestBody
    ): void => {
      queryClient.setQueriesData<GetInstructorsResult>(
        { queryKey: ['instructors', params.customerId] },
        old => {
          if (!old) return old;
          return {
            ...old,
            elements: old.elements.map(instructor => {
              if (instructor.instructorId !== params.instructorId)
                return instructor;
              return {
                ...instructor,
                programs: instructor.programs.map(program => ({
                  ...program,
                  disciplines: program.disciplines.map(discipline => {
                    if (
                      discipline.authorizationId !== variables.authorizationId
                    )
                      return discipline;
                    return {
                      ...discipline,
                      state: result.status,
                      caseId: result.caseId,
                    };
                  }),
                })),
              };
            }),
          };
        }
      );
    },
  });
}
